import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Box, Button, ButtonProps, IconButton, Menu, SvgIcon, SvgIconProps, Tooltip} from '@mui/material';
import {nanoid} from 'nanoid/non-secure';
import {ReactNode, useState} from 'react';
import {ComponentWithRequiredChildren, PropsWithRequiredChildren} from '../react/types';

export interface DropDownMenuProps extends PropsWithRequiredChildren, Pick<ButtonProps, 'color'> {
  label: string;
  menuDirection?: 'left' | 'right';
  menuIcon?: typeof SvgIcon;
  menuIconProps?: Omit<SvgIconProps, 'fontColor'>;
  title?: ReactNode;
  tooltip?: string;
  variant: 'icon' | 'text';
}

export const DropDownMenu: ComponentWithRequiredChildren<DropDownMenuProps> = ({
  children,
  color = 'inherit',
  label,
  menuDirection = 'left',
  menuIcon: MenuIcon = ArrowDropDownIcon,
  menuIconProps,
  title,
  tooltip,
  variant,
}: DropDownMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuButtonId = nanoid();
  const menuIcon = <MenuIcon {...menuIconProps} color={color} />;
  const menuButton =
    variant === 'icon' ? (
      <IconButton aria-label={label} color={color} id={menuButtonId} onClick={handleClick} size="small" sx={{p: 0}}>
        {title}
        {menuIcon}
      </IconButton>
    ) : (
      <Button
        aria-label={label}
        color={color}
        id={menuButtonId}
        onClick={handleClick}
        size="small"
        sx={{p: 0, textTransform: 'none'}}
      >
        {title}
        {menuIcon}
      </Button>
    );
  const menuButtonWithOptionalTooltip = tooltip ? <Tooltip title={tooltip}>{menuButton}</Tooltip> : menuButton;

  return (
    <>
      <Box sx={{alignItems: 'center', display: 'flex', flexDirection: 'row'}}>{menuButtonWithOptionalTooltip}</Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{horizontal: menuDirection === 'left' ? 'right' : 'left', vertical: 'bottom'}}
        key={`${label}::${menuOpen}`}
        onClick={handleClose}
        onClose={handleClose}
        open={menuOpen}
        slotProps={{
          list: {
            'aria-labelledby': menuButtonId,
          },
          paper: {
            elevation: 0,
            sx: {
              '&:before': {
                bgcolor: 'background.paper',
                content: '""',
                display: 'block',
                height: 10,
                left: menuDirection === 'right' ? 6 : undefined,
                position: 'absolute',
                right: menuDirection === 'left' ? 6 : undefined,
                top: 0,
                transform: 'translateY(-50%) rotate(45deg)',
                width: 10,
                zIndex: 0,
              },
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              overflow: 'visible',
            },
          },
        }}
        transformOrigin={{horizontal: menuDirection === 'left' ? 'right' : 'left', vertical: 'top'}}
      >
        {children}
      </Menu>
    </>
  );
};
